import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { MapPin, X } from 'lucide-react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface LocationDetails {
  placeId: string;
  formattedAddress: string;
  lat?: number;
  lng?: number;
}

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string, details?: LocationDetails) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const LocationAutocomplete = forwardRef<HTMLInputElement, LocationAutocompleteProps>(
  ({ value, onChange, placeholder, className, disabled }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
      const initGoogleMaps = async () => {
        try {
          const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
          if (!apiKey) {
            setLoadError('Google Maps API key not configured');
            return;
          }

          setOptions({
            key: apiKey,
            v: 'weekly',
            libraries: ['places'],
          });

          await importLibrary('places');
          setIsLoaded(true);
        } catch (error) {
          console.error('Failed to load Google Maps:', error);
          setLoadError('Failed to load Google Maps API');
        }
      };

      initGoogleMaps();
    }, []);

    useEffect(() => {
      // Disable Radix ui dialog pointer events lockout
      setTimeout(() => {
        document.body.style.pointerEvents = '';
      }, 0);
    });

    useEffect(() => {
      if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

      try {
        const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
          types: ['establishment', 'geocode'],
          componentRestrictions: { country: 'DE' },
          fields: ['place_id', 'formatted_address', 'name', 'geometry'],
        });

        const handlePlaceChanged = () => {
          const place = autocomplete.getPlace();

          if (place.formatted_address && place.place_id) {
            const displayName = place.formatted_address;
            const details: LocationDetails = {
              placeId: place.place_id,
              formattedAddress: place.formatted_address,
              lat: place.geometry?.location?.lat(),
              lng: place.geometry?.location?.lng(),
            };

            onChange(displayName, details);
          }
        };

        autocomplete.addListener('place_changed', handlePlaceChanged);
        autocompleteRef.current = autocomplete;

        return () => {
          if (autocompleteRef.current) {
            google.maps.event.clearInstanceListeners(autocompleteRef.current);
          }
        };
      } catch (error) {
        console.error('Failed to initialize autocomplete:', error);
        setLoadError('Failed to initialize location autocomplete');
      }
    }, [isLoaded, onChange]);

    useEffect(() => {
      if (inputRef.current && inputRef.current.value !== value) {
        inputRef.current.value = value;
      }
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    };

    const handleClear = () => {
      onChange('');
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.focus();
      }
    };

    if (loadError) {
      return (
        <div className="relative">
          <Input
            ref={ref}
            value={value}
            onChange={handleInputChange}
            placeholder={placeholder}
            className={className}
            disabled={disabled}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      );
    }

    return (
      <div className="relative">
        <Input
          ref={inputRef}
          defaultValue={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={cn('pr-16', className)}
          disabled={disabled || !isLoaded}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-1">
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0 hover:bg-transparent"
              onClick={handleClear}
              tabIndex={-1}
            >
              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </Button>
          )}
          <MapPin className={cn('h-4 w-4', isLoaded ? 'text-blue-500' : 'text-muted-foreground')} />
        </div>
        {!isLoaded && !loadError && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
            <div className="text-xs text-muted-foreground">Loading maps...</div>
          </div>
        )}
      </div>
    );
  },
);
